import EventsActions from "events/EventsEmitter";
import * as Utils from "services/UtilsService";
import { defaultGradients, ColorLegendDefault } from "constants/viewer";
import LayersActions from "./LayersActions";
import * as GlobalActions from "./GlobalActions";
import ViewerActions from "./ViewerActions";

export default class GradientActions extends EventsActions {
    static quantile: any = {
        layer: {},
        attributeIndex: -1,
        nbClass: 0,
        gradientId: "color",
        gradient: [],
        values: [],
    };
    static jenks: any = {
        layer: {},
        attributeIndex: -1,
        gradientId: "color",
        nbClass: 0,
        gradient: [],
        values: [],
    };

    static useAttributeGradient: boolean = false;
    static attributeGradient: any = [];
    static customGradient: any = [];

    static destroy() {
        GradientActions.quantile = {
            layer: {},
            attributeIndex: -1,
            nbClass: 0,
            gradientId: "color",
            gradient: [],
        };
        GradientActions.jenks = {
            layer: {},
            attributeIndex: -1,
            gradientId: "color",
            nbClass: 0,
        };
        GradientActions.useAttributeGradient = false;
        GradientActions.attributeGradient = [];
        GradientActions.customGradient = [];
        console.log("cleanup GradientActions done...");
    }

    static getInterpolatedColor(value: number, gradientId: string) {
        if (!gradientId) {
            gradientId = "color";
        }

        if (gradientId === "custom") {
            return GradientActions.customGradient;
        }

        let defaultGradient = defaultGradients[gradientId];
        if (value <= 0.0) return [defaultGradient[0].r, defaultGradient[0].g, defaultGradient[0].b];
        if (value >= 1.0)
            return [
                defaultGradient[defaultGradient.length - 1].r,
                defaultGradient[defaultGradient.length - 1].g,
                defaultGradient[defaultGradient.length - 1].b,
            ];
        var foundEntry = defaultGradient[0];
        var nextEntry = defaultGradient[0];
        for (var e in defaultGradient) {
            var entry = defaultGradient[e];
            if (entry.position >= value) {
                nextEntry = entry;
                break;
            }
            foundEntry = entry;
        }
        if (foundEntry === nextEntry) {
            return [
                defaultGradient[defaultGradient.length - 1].r,
                defaultGradient[defaultGradient.length - 1].g,
                defaultGradient[defaultGradient.length - 1].b,
            ];
        } else {
            var t: number = (value - foundEntry.position) / (nextEntry.position - foundEntry.position);
            var r: number = (1.0 - t) * foundEntry.r + t * nextEntry.r;
            var g: number = (1.0 - t) * foundEntry.g + t * nextEntry.g;
            var b: number = (1.0 - t) * foundEntry.b + t * nextEntry.b;
            return [r, g, b];
        }
    }

    static getAlphaGradient(layer: any, values: any) {
        const isAlpha = typeof layer["alpha_attribute"] !== "undefined" ? layer["alpha_attribute"] === "1" : false;

        if (!isAlpha) {
            return [];
        }
        let gradientId = layer["color_legend_alpha_gradient"];
        if (!gradientId) {
            gradientId = "color";
        }
        const reverseGradient =
            isAlpha && typeof layer["color_legend_reverse_alpha_gradient"] !== "undefined"
                ? layer["color_legend_reverse_alpha_gradient"] === "1"
                : false;
        const gradient: any = [];
        var color: any = [128, 128, 128];
        values.forEach((value: string, index: number) => {
            switch (value) {
                case "-":
                    gradient.push(
                        "rgb(" + parseInt(color[0]) + "," + parseInt(color[1]) + "," + parseInt(color[2]) + ")"
                    );
                    break;
                case "false":
                    color = this.getInterpolatedColor(reverseGradient ? 0 : 1, gradientId);
                    gradient.push(
                        "rgb(" + parseInt(color[0]) + "," + parseInt(color[1]) + "," + parseInt(color[2]) + ")"
                    );
                    break;
                case "true":
                    color = this.getInterpolatedColor(reverseGradient ? 1 : 0, gradientId);
                    gradient.push(
                        "rgb(" + parseInt(color[0]) + "," + parseInt(color[1]) + "," + parseInt(color[2]) + ")"
                    );
                    break;
                default:
                    color = this.getInterpolatedColor(index / values.length, gradientId);
                    if (color.find((entry: any) => typeof entry === "string" && entry.includes("rgb"))) {
                        gradient.push(color[index]);
                    } else {
                        gradient.push(
                            "rgb(" + parseInt(color[0]) + "," + parseInt(color[1]) + "," + parseInt(color[2]) + ")"
                        );
                    }
                    break;
            }
        });

        if (reverseGradient && gradient.length > 1) {
            gradient.reverse();
        }
        return gradient;
    }

    static getAttributeGradient(layer: any) {
        var result: any = [];
        const colorAttribute = layer["color_legend_attribute"];

        if (typeof colorAttribute === "undefined" || colorAttribute === "") {
            return result;
        }

        const isAlpha =
            typeof layer["alpha_attribute"] !== "undefined"
                ? layer["alpha_attribute"] === "1"
                : ColorLegendDefault.isAlpha;

        var gradientId = layer["color_legend_alpha_gradient"];
        if (typeof gradientId === "undefined") {
            gradientId = ColorLegendDefault.alphaColorGradientId;
            layer["color_legend_alpha_gradient"] = gradientId;
        }
        if (!isAlpha) {
            return []; //GradientActions.attributeGradient[layer.ID][colorAttribute];
        }

        var values = [];
        const attributes = GlobalActions.getAttributesObjects(layer["ID"]);
        const attribute = attributes.find((attribute: any) => attribute.name === colorAttribute);
        if (attribute && attribute.values) {
            values = attribute.values;
        }
        var gradient: any;
        if (gradientId === "custom") {
            if (GradientActions.customGradient.length > 0) {
                gradient = GradientActions.customGradient;
            } else {
                gradientId = "color";
                layer["color_legend_alpha_gradient"] = gradientId;
                gradient = GradientActions.getGradientColors(gradientId, 0, values.length, 1);
                GradientActions.customGradient = gradient;
            }
        } else {
            gradient = GradientActions.getGradientColors(gradientId, 0, values.length, 1);
        }
        if (gradient) {
            result = Utils.rgbToHSL(gradient);
        }
        return result;
    }

    static getGradientColors(
        gradientId: string,
        rangeMin: number,
        rangeMax: number,
        step: number,
        logarithm?: boolean,
        reverse?: boolean,
        interpolate?: boolean
    ) {
        let defaultGradient = defaultGradients[gradientId];
        if (rangeMax - rangeMin <= Number.EPSILON)
            return ["rgb(" + defaultGradient[0].r + "," + defaultGradient[0].g + "," + defaultGradient[0].b + ")"];
        let gradient = [];
        if (interpolate) {
            step = Number.EPSILON;
        }
        if (step <= Number.EPSILON) {
            step = (rangeMax - rangeMin) / 100.0;
        } else {
            let stepCount: number = (rangeMax - rangeMin) / step;
            if (stepCount > 100) step = (rangeMax - rangeMin) / 100.0;
        }
        for (let i: number = rangeMin; i < rangeMax; i += step) {
            let pos = i - rangeMin;
            var value = pos / (rangeMax - rangeMin);
            if (logarithm) {
                value = Utils.calculateLogarithm(value);
            }
            if (reverse) {
                value = 1.0 - value;
            }
            let color = this.getInterpolatedColor(value, gradientId);
            gradient.push("rgb(" + parseInt(color[0]) + "," + parseInt(color[1]) + "," + parseInt(color[2]) + ")");
        }
        return gradient;
    }

    getGradientColorSpace(gradientId: string, rangeMin: number, rangeMax: number, step: number, logarithm?: boolean) {
        let defaultGradient = defaultGradients[gradientId];
        if (rangeMax - rangeMin <= Number.EPSILON)
            return [
                {
                    color:
                        "rgb(" + defaultGradient[0].r + "," + defaultGradient[0].g + "," + defaultGradient[0].b + ")",
                    width: 1,
                    position: 1,
                    value: rangeMin,
                },
            ];
        let gradient = [];
        if (step <= Number.EPSILON) {
            step = (rangeMax - rangeMin) / 100.0;
        } else {
            let stepCount: number = (rangeMax - rangeMin) / step;
            if (stepCount > 100) step = (rangeMax - rangeMin) / 100.0;
        }
        let lastPosition: number = 0.0;
        for (let i: number = rangeMin; i < rangeMax; i += step) {
            var value = (i - rangeMin + 0.0) / (rangeMax - rangeMin);
            if (logarithm) {
                value = Utils.calculateLogarithm(value);
            }
            let position = (i - rangeMin + step + 0.0) / (rangeMax - rangeMin);
            if (position > 1.0) position = 1.0;
            let color = GradientActions.getInterpolatedColor(value, gradientId);
            gradient.push({
                color: "rgb(" + color[0] + "," + color[1] + "," + color[2] + ")",
                width: position - lastPosition,
                value: i,
                position: position,
            });
            lastPosition = position;
        }
        return gradient;
    }

    static getSettings(layer: any) {
        const settings = GlobalActions.getColorLegendSettings(layer);

        let colorRangeMin: number = settings.colorRangeMin;
        let colorRangeMax: number = settings.colorRangeMax;
        if (settings.minMaxData) {
            const extendedMeta = ViewerActions.extendedMeta[layer["ID"]];
            if (extendedMeta) {
                const minMax: any = GlobalActions.getDataMinMaxValue(extendedMeta, settings.colorAttributeIndex);
                if (minMax.minOk) {
                    colorRangeMin = Utils.round2Decimals(parseFloat(minMax.min));
                }
                if (minMax.maxOk) {
                    colorRangeMax = Utils.round2Decimals(parseFloat(minMax.max));
                }
            }
        }
        let logarithmic = false;
        if (settings.dataDrivenMethod === 1 && settings.dataDrivenEqualMode === 1) {
            logarithmic = true;
        }
        return {
            attributeIndex: settings.colorAttributeIndex,
            legendMode: settings.colorMode,
            gradientId: settings.colorGradientId,
            alphaGradientId: settings.alphaColorGradientId,
            step: settings.colorRangeStep,
            rangeMin: colorRangeMin,
            rangeMax: colorRangeMax,
            rangeCount: settings.colorRangeCount,
            dataDrivenMethod: settings.dataDrivenMethod,
            dataDrivenEqualMode: settings.dataDrivenEqualMode,
            reverseGradient: settings.reverseGradient,
            reverseAlphaGradient: settings.reverseAlphaGradient,
            interpolateGradient: settings.interpolateGradient,
            logarithmic: logarithmic,
            isAlpha: settings.isAlpha,
        };
    }

    static getLayerGradient(layer: any, isAlpha?: boolean) {
        var gradient: any = [];
        var legendMode = layer["color_legend_mode"] || ColorLegendDefault.colorMode;
        var gradientId = isAlpha
            ? layer["color_legend_alpha_gradient"] || ColorLegendDefault.alphaColorGradientId
            : layer["color_legend_gradient"] || ColorLegendDefault.colorGradientId;
        var step =
            typeof layer["color_legend_range_step"] !== "undefined"
                ? parseFloat(layer["color_legend_range_step"])
                : ColorLegendDefault.colorRangeStep;
        var rangeMin =
            typeof layer["color_legend_range_min"] !== "undefined"
                ? parseFloat(layer["color_legend_range_min"])
                : ColorLegendDefault.colorRangeMin;
        var rangeMax =
            typeof layer["color_legend_range_max"] !== "undefined"
                ? parseFloat(layer["color_legend_range_max"])
                : ColorLegendDefault.colorRangeMax;
        var rangeCount =
            typeof layer["color_legend_range_count"] !== "undefined"
                ? parseInt(layer["color_legend_range_count"])
                : ColorLegendDefault.colorRangeCount;
        var dataDrivenMethod =
            typeof layer["color_legend_data_driven_method"] !== "undefined"
                ? parseInt(layer["color_legend_data_driven_method"])
                : ColorLegendDefault.dataDrivenMethod;
        var dataDrivenEqualMode =
            typeof layer["color_legend_data_driven_equal_mode"] !== "undefined"
                ? parseInt(layer["color_legend_data_driven_equal_mode"])
                : ColorLegendDefault.dataDrivenEqualMode;
        const reverseGradient: boolean = isAlpha
            ? layer["color_legend_reverse_alpha_gradient"] === "1"
            : layer["color_legend_reverse_gradient"] === "1";
        const interpolateGradient: boolean = layer["color_legend_interpolate_gradient"] === "1";
        const defaultGradient = defaultGradients[gradientId];
        var logarithm = false;
        if (legendMode === "1") {
            if (
                typeof layer["color_legend_static_r"] === "string" &&
                typeof layer["color_legend_static_g"] === "string" &&
                typeof layer["color_legend_static_b"] === "string"
            ) {
                gradient = [
                    {
                        color:
                            "rgb(" +
                            layer.color_legend_static_r +
                            "," +
                            layer.color_legend_static_g +
                            "," +
                            layer.color_legend_static_b +
                            ")",
                        width: 1,
                        position: 1,
                        value: rangeMin,
                    },
                ];
            }
            return gradient;
        }
        if (dataDrivenMethod > 0) {
            step = (rangeMax - rangeMin) / rangeCount;
            if (dataDrivenMethod === 1 && dataDrivenEqualMode === 1) {
                logarithm = true;
            }
        }

        if (rangeMax - rangeMin <= Number.EPSILON)
            return [
                {
                    color:
                        "rgb(" + defaultGradient[0].r + "," + defaultGradient[0].g + "," + defaultGradient[0].b + ")",
                    width: 1,
                    position: 1,
                    value: rangeMin,
                },
            ];
        if (interpolateGradient) {
            step = Number.EPSILON;
        }
        if (step <= Number.EPSILON) {
            step = (rangeMax - rangeMin) / 100.0;
        } else {
            let stepCount: number = (rangeMax - rangeMin) / step;
            if (stepCount > 100) step = (rangeMax - rangeMin) / 100.0;
        }
        let lastPosition: number = 0.0;
        for (let i: number = rangeMin; i < rangeMax; i += step) {
            var value = (i - rangeMin + 0.0) / (rangeMax - rangeMin);
            var label = i;
            if (logarithm) {
                label = Utils.logScale(value, rangeMin, rangeMax);
            }
            if (reverseGradient) {
                value = 1.0 - value;
            }
            let position = (i - rangeMin + step + 0.0) / (rangeMax - rangeMin);
            if (position > 1.0) position = 1.0;

            let color = this.getInterpolatedColor(value, gradientId);
            gradient.push({
                color: "rgb(" + color[0] + "," + color[1] + "," + color[2] + ")",
                width: position - lastPosition,
                value: label,
                position: position,
            });
            lastPosition = position;
        }
        //console.log('gradient:', gradient);
        return gradient;
    }

    static getHistogram(layerID: string, colorAttributeIndex: number) {
        if (colorAttributeIndex > -1) {
            var data = LayersActions.data;
            if (Object.values(data).length > 0) {
                data = data[layerID];
                if (data) {
                    var entries = Object.entries(data).filter((prop: any) =>
                        prop[0].includes("attribute" + colorAttributeIndex + "_hist_weight")
                    );
                    return entries.map((entry: any) => {
                        return parseFloat(entry[1]);
                    });
                }
                return [];
            }
        }
        return [];
    }

    static getLayerColors(layer: any) {
        const settings = GradientActions.getSettings(layer);

        var gradient: any = ["rgb(128, 128, 128)"];
        switch (settings.legendMode) {
            case 1:
                const r: number =
                    typeof layer["color_legend_static_r"] !== "undefined"
                        ? parseInt(layer["color_legend_static_r"])
                        : 128;
                const g: number =
                    typeof layer["color_legend_static_g"] !== "undefined"
                        ? parseInt(layer["color_legend_static_g"])
                        : 128;
                const b: number =
                    typeof layer["color_legend_static_b"] !== "undefined"
                        ? parseInt(layer["color_legend_static_b"])
                        : 128;
                gradient = ["rgb(" + r + "," + g + "," + b + ")"];
                break;
            case 2:
                let gradientId = settings.isAlpha ? settings.alphaGradientId : settings.gradientId;
                if (typeof gradientId === "undefined") gradientId = "color";
                if (gradientId === "custom") {
                    return GradientActions.customGradient.length > 0 ? GradientActions.customGradient : gradient;
                }

                const histogram = this.getHistogram(layer.ID, settings.attributeIndex);
                switch (settings.dataDrivenMethod) {
                    case 0:
                        gradient = this.getGradientColors(
                            gradientId,
                            settings.rangeMin,
                            settings.rangeMax,
                            settings.step,
                            false,
                            settings.isAlpha ? settings.reverseAlphaGradient : settings.reverseGradient,
                            settings.interpolateGradient
                        );
                        break;
                    case 1:
                        if (settings.dataDrivenEqualMode === 0) {
                            gradient = this.getGradientColors(
                                gradientId,
                                settings.rangeMin,
                                settings.rangeMax,
                                (settings.rangeMax - settings.rangeMin) / settings.rangeCount,
                                false,
                                settings.isAlpha ? settings.reverseAlphaGradient : settings.reverseGradient,
                                settings.interpolateGradient
                            );
                        } else {
                            gradient = this.getGradientColors(
                                gradientId,
                                settings.rangeMin,
                                settings.rangeMax,
                                (settings.rangeMax - settings.rangeMin) / settings.rangeCount,
                                true,
                                settings.isAlpha ? settings.reverseAlphaGradient : settings.reverseGradient,
                                settings.interpolateGradient
                            );
                        }
                        break;
                    case 2:
                        gradient = this.getQuantileGradient(histogram, layer);
                        gradient = gradient.map((entry: any) => {
                            return entry.color;
                        });
                        break;
                    case 3:
                        gradient = this.getJenksGradient(histogram, layer);
                        gradient = gradient.map((entry: any) => {
                            return entry.color;
                        });
                        break;
                }
                return gradient;
        }
        return gradient;
    }

    static getDistribution(histogram: any, layer: any) {
        var gradients;
        var methodType: number = GlobalActions.getLayerNumericProperty(layer, "color_legend_data_driven_method", 0);
        switch (methodType) {
            case 0:
            case 1:
                return [];
            case 2:
                gradients = GradientActions.getQuantileGradient(histogram, layer);
                break;
            case 3:
                gradients = GradientActions.getJenksGradient(histogram, layer);
                break;
        }
        return gradients.map((gradient: any) => {
            return gradient.value;
        });
    }

    static getQuantileGradient(histogram: any, layer: any) {
        var settings = GradientActions.getSettings(layer);
        var data: any = [...histogram];
        var { gradientId, rangeMin, rangeMax, rangeCount, attributeIndex, reverseGradient, interpolateGradient } =
            settings;
        var gradient: any = [];
        if (interpolateGradient) {
            rangeCount = data.length;
        }
        var nbClass: number = rangeCount;
        if (nbClass > 100 || data.length === 0) {
            return [{ color: "rgb(128,128,128)", width: 1.0, value: 0, position: 0 }];
        }
        const processValues = (values: any) => {
            const stepWidth = 1 / rangeCount;
            var curGradient: any = [];
            values.forEach((obj: any, index: number) => {
                var position = index / values.length;
                if (reverseGradient) {
                    position = 1 - position;
                }
                var color = GradientActions.getInterpolatedColor(position, gradientId);
                curGradient.push({
                    color: "rgb(" + parseInt(color[0]) + "," + parseInt(color[1]) + "," + parseInt(color[2]) + ")",
                    width: stepWidth,
                    value: rangeMin + (obj.index / data.length) * (rangeMax - rangeMin),
                    position: position,
                });
            });
            return curGradient;
        };
        if (
            layer.ID === this.quantile.layer.ID &&
            attributeIndex === this.quantile.attributeIndex &&
            nbClass === this.quantile.nbClass &&
            gradientId === this.quantile.gradientId &&
            this.quantile.values.length > 0
        ) {
            //console.log("this.quantile:", this.quantile);
            gradient = processValues(this.quantile.values);
        }
        var sumArray = Utils.sumArray(data);
        var sum = 0;
        var tmp: any = [],
            bounds: any = [];
        var accumSum: any = [];
        var delta = data.length / 100;

        const getChunkSize = (i: number, distribution = 0) => {
            if (distribution === 0) {
                return sumArray * (i / nbClass);
            } else {
                return sumArray * (i / nbClass); // potential calculation for logarithmic
            }
        };
        const findIndex = (arr: any, value: number) => {
            return arr.findIndex((val: number) => val > value);
        };
        data.forEach((val: number, index: number) => {
            sum += val * delta;
            accumSum[index] = sum;
        });
        for (var i = 0; i < nbClass; i++) {
            var chunkSize = getChunkSize(i);
            var index = findIndex(accumSum, chunkSize);
            if (index > -1) {
                tmp[i] = { index: index, percentage: chunkSize };
            }
        }
        for (var j = 0; j < tmp.length; j++) {
            var obj: any = tmp[j];
            bounds.push({
                width: obj["index"] - (tmp[j - 1] ? tmp[j - 1]["index"] : 0),
                index: obj["percentage"] * 100,
            });
        }
        //console.log('bounds:', bounds);
        gradient = processValues(bounds);
        //console.log("gradient:", gradient);
        this.quantile = {
            ...{},
            layer: layer,
            gradientId: gradientId,
            nbClass: nbClass,
            attributeIndex: attributeIndex,
            gradient: gradient,
            values: bounds,
        };
        //console.log("original.this.quantile:", this.quantile);
        return gradient;
    }

    static getJenksGradient(histogram: any, layer: any) {
        var settings = GradientActions.getSettings(layer);
        var data: any = [...histogram];
        var gradient: any = [];
        var { gradientId, rangeMin, rangeMax, rangeCount, attributeIndex, reverseGradient, interpolateGradient } =
            settings;
        if (interpolateGradient) {
            rangeCount = data.length;
        }
        var nbClass: number = rangeCount;

        if (nbClass > 100 || data.length === 0) {
            return [{ color: "rgb(128,128,128)", width: 1.0, value: 0, position: 0 }];
        }
        const processValues = (values: any) => {
            const stepWidth = 1 / rangeCount;
            var curGradient: any = [];
            values.forEach((obj: any, index: number) => {
                var position = index / values.length;
                if (reverseGradient) {
                    position = 1 - position;
                }
                var color = GradientActions.getInterpolatedColor(position, gradientId);
                curGradient.push({
                    color: "rgb(" + parseInt(color[0]) + "," + parseInt(color[1]) + "," + parseInt(color[2]) + ")",
                    width: stepWidth,
                    value: rangeMin + (obj.index / data.length) * (rangeMax - rangeMin),
                    position: position,
                });
            });
            return curGradient;
        };
        if (
            layer.ID === this.jenks.layer.ID &&
            gradientId === this.jenks.gradientId &&
            attributeIndex === this.jenks.attributeIndex &&
            nbClass === this.jenks.nbClass &&
            this.jenks.values.length > 0
        ) {
            //console.log("this.jenks:", this.jenks);
            gradient = processValues(this.jenks.values);
            return gradient;
        }
        var dataList = Utils.sortArray([...data]);
        //console.log("dataList:", dataList);
        const findIndex = (min: number, max: number) => {
            var index = -1;
            var result: any = [];
            dataList.forEach((value: number, idx: number) => {
                if (value >= min && value <= max) {
                    result.push(idx);
                }
            });
            var curIndex = Utils.minValueArray(result);
            if (typeof curIndex !== "undefined") {
                index = curIndex;
            }
            return index;
        };
        var values: any = [],
            ranges: any = [],
            bounds: any = [];

        var matrices = Utils.jenksMatrices(dataList, nbClass),
            // we only need lower class limits here
            lower_class_limits = matrices.lower_class_limits,
            k = dataList.length - 1,
            kclass: any = [],
            countNum = nbClass;
        //console.log("matrices:", matrices);
        kclass = new Array(nbClass);
        kclass = kclass.fill(0);
        kclass[0] = dataList[0];
        kclass[nbClass] = dataList[data.length - 1];
        while (countNum > 1 && k > -1) {
            if (k > -1 && data[lower_class_limits[k][countNum] - 2]) {
                kclass[countNum - 1] = data[lower_class_limits[k][countNum] - 2];
            }
            k = lower_class_limits[k][countNum] - 1;
            countNum--;
        }
        bounds = Utils.sortArray(kclass);
        //console.log("kclass:", kclass);

        for (var i = 0; i < bounds.length - 1; i++) {
            ranges[i] = bounds[i] + "-" + bounds[i + 1];
        }

        //console.log("ranges:", ranges);
        var lastIndex = 0;
        for (var j = 0; j < ranges.length; j++) {
            var range = ranges[j].split("-");
            var min = parseFloat(range[0]);
            var max = parseFloat(range[1]);
            var index = findIndex(min, max);
            if (index > -1) {
                values.push({ index: index, value: dataList[index] });
                lastIndex = index;
            } else {
                values.push({ index: lastIndex, value: max });
            }
        }
        //console.log("values:", values);
        gradient = processValues(values);
        this.jenks = {
            layer: layer,
            gradientId: gradientId,
            nbClass: nbClass,
            attributeIndex: attributeIndex,
            gradient: gradient,
            values: values,
        };
        return gradient;
    }
}
